import { useCallback, useRef } from "react";
import { logUserInteraction } from "../utils/logUserInteraction";

/* =====================================================
   PAGE CONFIG
===================================================== */

const PAGE_CONFIG = {
  avgactivetv: { hasTimeframe: true },
  avgactivevw: { hasTimeframe: true },
  totalviewerstr: { hasTimeframe: true },
  switchin: { hasTimeframe: true },
  tuneinout: { hasTimeframe: true },

  overview: { hasTimeframe: false },
  uniquetv: { hasTimeframe: false },
  avgvidconsumption: { hasTimeframe: false },
  channelgrid: { hasTimeframe: false },
  channelgridcomparison: { hasTimeframe: false },
  segmentanalysis: { hasTimeframe: false },
  socialmediacompare: { hasTimeframe: false },
  crosscomparison: { hasTimeframe: false },
  pastperformance: { hasTimeframe: false },
  managementreport: { hasTimeframe: false },
  location: { hasTimeframe: false },
  inteltube: { hasTimeframe: false },
};

/* =====================================================
   MODE MAP
===================================================== */

const MODE_MAP = {
  minute: { value: -1, label: "minute" },
  hour:   { value: 0,  label: "hour" },
  day:    { value: 1,  label: "day" },
  week:   { value: 2,  label: "week" },
  month:  { value: 3,  label: "month" },
  epg:    { value: 4,  label: "epg" },
};

/* =====================================================
   HELPERS
===================================================== */

function getUserDefaultMode() {
  try {
    const u = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
    return u.chartdefault || u.chartDefault || "minute";
  } catch {
    return "minute";
  }
}

function resolveInitialTimeframe(chartType) {
  const cfg = PAGE_CONFIG[chartType];
  if (!cfg || !cfg.hasTimeframe) {
    return { value: 99, label: "fixed" };
  }

  const mode = getUserDefaultMode();
  return MODE_MAP[mode] || MODE_MAP.minute;
}

/* =====================================================
   HOOK
===================================================== */

export function useUserJourneyLogger({
  channelId,
  channelName,
  userLanguage = "en",
}) {
  const loggedPagesRef = useRef({});
  const lastTimeframeRef = useRef({});

  /* ---------- PAGE CHANGE ---------- */
  const logPageChange = useCallback(
    ({ chartType }) => {
      if (!chartType || !channelId) return;

      const key = `${chartType}-${channelId}`;
      if (loggedPagesRef.current[key]) return;

      loggedPagesRef.current[key] = true;

      const tf = resolveInitialTimeframe(chartType);

      logUserInteraction({
        actionType: "pagetype_changed",
        chartType,
        timeframeValue: tf.value,
        details: `Entered ${chartType} (${tf.label})`,
        channelId,
        channelName,
        userLanguage,
      });
    },
    [channelId, channelName, userLanguage]
  );

  /* ---------- TIMEFRAME CHANGE ---------- */
  const logTimeframeChange = useCallback(
    ({ chartType, mode }) => {
      if (!chartType || !mode || !channelId) return;

      const tf = MODE_MAP[mode];
      if (!tf) return;

      const key = `${chartType}-${channelId}`;
      if (lastTimeframeRef.current[key] === tf.value) return;

      lastTimeframeRef.current[key] = tf.value;

      logUserInteraction({
        actionType: "timeframe_changed",
        chartType,
        timeframeValue: tf.value,
        details: `Timeframe changed to ${tf.label}`,
        channelId,
        channelName,
        userLanguage,
      });
    },
    [channelId, channelName, userLanguage]
  );

  return {
    logPageChange,
    logTimeframeChange,
  };
}
